import { useMutation, useQueryClient } from "@tanstack/react-query";

import httpClient from "@/config/axios";
import { useScheduleStore, type CurDate } from "@/store/scheduleStore";
import { getConfigIdForVersion } from "../service/getConfigIdForVersion";
import {
  getInfeasibilityFromError,
  type Infeasibility,
} from "@/types/roster-infeasibility";
import { invalidateRosterCachesAndSelectLatestSchedule } from "../service/invalidateRosterCreateCaches";
import type { RosterCreateConfigPayload } from "../service/rosterCreateConfigPayload";
import {
  clearPendingRosterCreateConfigSyncCache,
  invalidateRosterCreateConfigSyncCaches,
  markPendingRosterCreateConfigSyncCache,
  type RosterCreateConfigSyncCacheTarget,
} from "../service/rosterCreateConfigSyncCache";

export type {
  InfeasibilitySeverity,
  ViolationSample,
  PreflightIssue,
  Infeasibility,
  ResolutionOption,
  ResolutionCause,
  FixLocation,
} from "@/types/roster-infeasibility";

interface GenerateRosterParams {
  gradeStrategy: string;
  curDate: CurDate | null;
  currentConfigVer: number | null;
  configIdOverride?: number | null;
  config?: RosterCreateConfigPayload | null;
  isAdmin: boolean;
  selectedGroupId: string | null;
  /** 고도화 생성 — 기본 false */
  advancedInference?: boolean;
  /** 실패 후 유저가 고른 해결 옵션의 config delta — 이번 생성에만 override(DB 미변경) */
  configOverride?: Record<string, unknown> | null;
  /** ontology 옵션 재생성용 */
  treatmentIds?: string[] | null;
  /** per-nurse 주말휴무 해제 재생성용 — 해당 nurse_id 들의 주말휴무를 해제(period)하고 재생성 */
  weekendOffRelease?: string[] | null;
  /** per-nurse 월 야간 한도 하향 재생성용 — 해당 간호사의 n_exact/n_max 를 낮추고 재생성 */
  monthlyLimitRelease?: Array<{
    nurse_id: string;
    field: "n_exact" | "n_max";
    value: number;
  }> | null;
  /** per-nurse 금지근무 해제 재생성용 — 겹치는 날의 금지 셀을 해제(is_applied=False)하고 재생성 */
  bannedWantedRelease?: Array<{ nurse_id: string; days: number[] }> | null;
  /** per-nurse 근무유형 추가 재생성용 — allowed_shifts 에 D/E 등을 합집합(period)하고 재생성 */
  allowedShiftAdd?: Array<{ nurse_id: string; add: string[] }> | null;
}

export interface GenerateRosterResponse {
  schedule_id?: string;
  job_id?: string;
  infeasibility?: Infeasibility | null;
  materialized_config?: {
    config_id: number;
    version?: number | null;
    config_name?: string | null;
    config_memo?: string | null;
  } | null;
  configs?: {
    config_id?: number | null;
  } | null;
}

export class RosterInfeasibleError extends Error {
  infeasibility: Infeasibility;

  constructor(infeasibility: Infeasibility) {
    super(infeasibility.summary_message_ko || "근무표를 만들 수 없습니다.");
    this.name = "RosterInfeasibleError";
    this.infeasibility = infeasibility;
  }
}

const endPointer = () => {
  return import.meta.env.VITE_ENV === "LOCAL"
    ? `/roster_create/generate`
    : `/roster_create/async`;
};

const isLocalGenerate = () => import.meta.env.VITE_ENV === "LOCAL";

export default function useGenerateRosterMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      gradeStrategy,
      curDate: dateParam,
      currentConfigVer,
      config,
      configIdOverride,
      isAdmin,
      selectedGroupId,
      advancedInference = false,
      configOverride = null,
      treatmentIds = null,
      weekendOffRelease = null,
      monthlyLimitRelease = null,
      bannedWantedRelease = null,
      allowedShiftAdd = null,
    }: GenerateRosterParams) => {
      const selectedAlgorithm = "cp_sat"; /* 근무표 생성 > 생성 알고리즘 */

      // 서버검증
      const configId = config?.config_id
        ? config.config_id
        : configIdOverride
          ? configIdOverride
          : currentConfigVer
            ? await getConfigIdForVersion(
                { configVersion: currentConfigVer },
                { isAdmin },
                { selectedGroupId: selectedGroupId ?? "" }
              )
            : null;

      const endpoint = endPointer();

      try {
        const response = await httpClient.post<GenerateRosterResponse>(
          endpoint,
          {
            year: dateParam?.year,
            month: dateParam?.month,
            algorithm: selectedAlgorithm,
            config_id: configId,
            config: config ?? undefined,
            grade_strategy: gradeStrategy,
            advanced_inference: advancedInference,
            // 해결책 재생성: 고른 옵션의 config delta / treatment_ids 를 이번 생성에만 주입.
            // 일반 생성이면 undefined → 백엔드 기존 동작 동일.
            config_override: configOverride ?? undefined,
            treatment_ids: treatmentIds ?? undefined,
            weekend_off_release: weekendOffRelease ?? undefined,
            monthly_limit_release: monthlyLimitRelease ?? undefined,
            banned_wanted_release: bannedWantedRelease ?? undefined,
            allowed_shift_add: allowedShiftAdd ?? undefined,
            // 대상 병동: 백엔드가 req.group_id 로 생성 대상 그룹을 해석(미전송 시 home 폴백).
            // HN 이 home 아닌 관리 병동을 선택해 생성할 때 필수.
            group_id: selectedGroupId || undefined,
          },
          {
            actionTrack: {
              event: "roster_create.roster.generate",
              section: "roster_create",
              props: {
                year: dateParam?.year,
                month: dateParam?.month,
                mode: isLocalGenerate() ? "sync" : "async",
                has_config_payload: Boolean(config),
                has_config_id: Boolean(configId),
                grade_strategy: gradeStrategy,
                advanced_inference: advancedInference,
                group_id: selectedGroupId || undefined,
              },
            },
          }
        );

        return response;
      } catch (error) {
        const infeasibility = getInfeasibilityFromError(error);
        if (infeasibility?.severity === "blocking") {
          throw new RosterInfeasibleError(infeasibility);
        }
        throw error;
      }
    },
    onSuccess: async (data, variables) => {
      const queryGroupId = variables.isAdmin
        ? (variables.selectedGroupId ?? "")
        : "";
      const materializedConfigId =
        data?.materialized_config?.config_id ?? data?.configs?.config_id ?? null;
      const effects = {
        nurses: Boolean(
          variables.weekendOffRelease?.length ||
            variables.monthlyLimitRelease?.length ||
            variables.allowedShiftAdd?.length
        ),
        monthlyLimits: Boolean(variables.monthlyLimitRelease?.length),
        wanted: Boolean(variables.bannedWantedRelease?.length),
        weeklyOff: Boolean(variables.weekendOffRelease?.length),
      };
      const hasResolutionEffects = Object.values(effects).some(Boolean);
      const shouldSyncConfig = Boolean(
        materializedConfigId != null ||
          variables.config ||
          variables.configOverride ||
          hasResolutionEffects
      );

      if (shouldSyncConfig) {
        const target: RosterCreateConfigSyncCacheTarget = {
          queryGroupId,
          jobId: data?.job_id ?? null,
          materializedConfigId,
          year: variables.curDate?.year,
          month: variables.curDate?.month,
          effects,
        };

        if (isLocalGenerate()) {
          await invalidateRosterCreateConfigSyncCaches(queryClient, target);
        } else {
          markPendingRosterCreateConfigSyncCache(target);
        }
      }

      if (!isLocalGenerate()) return;

      const year = variables.curDate?.year;
      const month = variables.curDate?.month;
      if (year != null && month != null) {
        await invalidateRosterCachesAndSelectLatestSchedule(
          queryClient,
          {
            year,
            month,
            selectedGroupId: variables.selectedGroupId,
            isAdmin: variables.isAdmin,
          },
          useScheduleStore.getState().setCurScheduleInfo
        );
      }

      alert("generate Success");
    },
    onError: () => {
      clearPendingRosterCreateConfigSyncCache();
    },
  });
}
